import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {ColorValue, LightType} from "#template/nodes/types"
import {colorValue, lightType} from "#template/nodes/validation"
import {LightNode} from "#template/nodes/object/light/light-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {pointLightRadiance} from "#template/utils/light-intensity"
import {visitNone} from "#template/template-node/visit-helpers"

export type PointLightParameters = {
    name: string
    intensity: number
    radius: number
    color: ColorValue
    on: boolean
    visibleDirectly: boolean
    visibleInReflections: boolean
    visibleInRefractions: boolean
    lightType: LightType
} & ObjectNodeParameters

const pointLightDefinition: ObjectNodeDefinition<PointLightParameters> = {
    nodeClass: "PointLight",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                intensity: z.number(),
                radius: z.number(),
                color: colorValue,
                on: z.boolean(),
                visibleDirectly: z.boolean(),
                visibleInReflections: z.boolean(),
                visibleInRefractions: z.boolean(),
                lightType: lightType,
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class PointLight extends LightNode<PointLightParameters> {
    public override get definition() {
        return pointLightDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<PointLightParameters> {
        const {evaluator} = context
        const {radius, color, on, intensity, visibleDirectly, visibleInReflections, visibleInRefractions, lightType} = this.parameters

        const scope = evaluator.getScope(this)

        const mappedIntensity = lightType === "Corona" ? intensity : pointLightRadiance(intensity, radius)

        this.setupObject(scope, context, "PointLight", undefined, undefined, (objectProps) => {
            return {
                preDisplayList: scope.struct<SceneNodes.PointLight>("PointLight", {
                    type: "PointLight",
                    ...objectProps,
                    radius,
                    color,
                    on,
                    intensity: mappedIntensity,
                    visibleDirectly,
                    visibleInReflections,
                    visibleInRefractions,
                }),
            }
        })

        return visitNone(this.parameters)
    }
}
